import { Injectable } from '@angular/core';
import { ApiService } from '../../api/api.service';
import { map, Observable, shareReplay } from 'rxjs';
import { MenuItem } from 'src/app/components/sidebar/menu-item';

@Injectable({
  providedIn: 'root',
})
export class SidebarService {
  protected endpoint = 'menu/sidebar';

  private menuOptions: MenuItem[] = [];
  public showProdMenu = false;
  private activeTab: number = 0;
  private sidebarCache = new Map<string, Observable<MenuItem[]>>();

  constructor(private apiService: ApiService) {}

  /**
   * Fetches sidebar menu, filters by environment, builds hierarchy, and caches it.
   */
  getSidebar(audience: string = 'customer'): Observable<MenuItem[]> {
    if (!this.sidebarCache.has(audience)) {
      const cached$ = this.apiService
        .get(this.endpoint!, { audience }, true)
        .pipe(
          map((res: any) => {
            let flatMenuData: MenuItem[] = Array.isArray(res?.data)
              ? res.data
              : [];

            // Transform 'accounts' to 'Instant Rate Manager'
            flatMenuData = flatMenuData.map((item: any) => {
              if (item.route === 'accounts' || item.route === '/accounts') {
                return {
                  ...item,
                  name: 'Instant Rate Manager',
                  route: 'instant-rate-manager',
                };
              }
              if (
                item.route &&
                item.route.includes('accounts/instant-rates-manager')
              ) {
                return {
                  ...item,
                  route: item.route
                    .replace(
                      'accounts/instant-rates-manager',
                      'instant-rate-manager',
                    )
                    .replace(/\/$/, ''),
                };
              }
              return item;
            });

            const filtered = flatMenuData.filter((menu) =>
              this.showProdMenu ? menu.environment === 'Prod' : true,
            );

            const hierarchical = this.buildHierarchy(filtered);
            this.menuOptions = hierarchical;

            return hierarchical;
          }),
          shareReplay(1),
        );
      this.sidebarCache.set(audience, cached$);
    }
    return this.sidebarCache.get(audience)!;
  }

  /**
   * Returns the current menu tree (after it's been fetched)
   */
  getCachedMenu(): MenuItem[] {
    return this.menuOptions;
  }

  /**
   * Determines the active tab index based on current route
   */
  setActiveTab(): void {
    const currentPage = window.location.pathname;

    const findTabIndex = (menu: MenuItem, index: number): number | null => {
      if (menu.route === currentPage || menu.route === currentPage.slice(1)) {
        return index;
      }

      for (const child of menu.children || []) {
        const childIndex = findTabIndex(child, index);
        if (childIndex !== null) {
          return childIndex;
        }
      }

      return null;
    };

    this.activeTab = this.menuOptions.findIndex((menu, index) => {
      return findTabIndex(menu, index) !== null;
    });
  }

  /**
   * Returns the active tab index
   */
  getActiveTab(): number {
    return this.activeTab;
  }

  /**
   * Sorts menu items by their `order` value
   */
  private sortByMenuOrder(arr: MenuItem[]): MenuItem[] {
    return arr.sort((a, b) => {
      const orderA = parseInt(a.order as any, 10);
      const orderB = parseInt(b.order as any, 10);

      if (isNaN(orderA) && isNaN(orderB)) return 0;
      if (isNaN(orderA)) return 1;
      if (isNaN(orderB)) return -1;

      return orderA - orderB;
    });
  }

  /**
   * Converts a flat list of menu items into a nested hierarchy
   */
  private buildHierarchy(flatData: MenuItem[]): MenuItem[] {
    const menuMap: { [key: string]: MenuItem } = {};
    const rootMenu: MenuItem[] = [];

    flatData.forEach((item) => {
      menuMap[item.id] = { ...item, children: [] };
    });

    flatData.forEach((item) => {
      const current = menuMap[item.id];
      const parentId = String(item.parent_id);
      const parent = menuMap[parentId];

      if (parentId === '0') {
        rootMenu.push(current);
      } else if (parent) {
        parent.children?.push(current);
      }
      // Skip orphans: parent not in this audience/menu set (e.g. provider menu
      // includes child items whose parent is customer-only).
    });

    Object.values(menuMap).forEach((item) => {
      if (item.children?.length) {
        item.children = this.sortByMenuOrder(item.children);
      }
    });

    return this.sortByMenuOrder(rootMenu);
  }
}
